import clsx from 'clsx';
import { forwardRef, useEffect, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import { _MIN_CONTAINER_WIDTH } from './empty-state-utils.js';
import { emptyStateContainerCss } from './EmptyState.sty.js';
import type { EmptyStateProps } from './types/empty-state-types.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Flex } from '../../layouts/flex/Flex.js';

const Monitored = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps & {
    /** Allows behavior tracking. */
    'data-dt-component'?: string;
  }
>((props, forwardedRef) => {
  const {
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-dt-component': dataDtComponent,
    ...remainingProps
  } = props;

  const emptyStateContainerRef = useRef<HTMLDivElement>(null);
  const emptyStateRef = useMergeRefs([emptyStateContainerRef, forwardedRef]);

  const [emptyStateParentContainer, setEmptyStateParentContainer] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    if (emptyStateContainerRef.current) {
      setEmptyStateParentContainer(
        emptyStateContainerRef.current.parentElement,
      );
    }
  }, []);

  // assign the observer to the parent container of the component
  const parentContainerRef = useResizeObserver({
    ref: emptyStateParentContainer,
  });
  const parentContainerWidth = parentContainerRef.width ?? 0;

  const isInResponsive =
    parentContainerWidth < _MIN_CONTAINER_WIDTH && size === 'large';

  return (
    <Flex
      ref={emptyStateRef}
      className={clsx([
        emptyStateContainerCss({
          size: isInResponsive ? 'largeResponsive' : size,
        }),
        consumerClassName,
      ])}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingProps}
      data-dt-component={dataDtComponent}
    >
      {children}
    </Flex>
  );
});

const Unmonitored = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps & {
    /** Allows behavior tracking. */
    'data-dt-component'?: string;
  }
>((props, forwardedRef) => {
  const {
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-dt-component': dataDtComponent,
    ...remainingProps
  } = props;

  return (
    <Flex
      ref={forwardedRef}
      className={clsx([emptyStateContainerCss({ size }), consumerClassName])}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingProps}
      data-dt-component={dataDtComponent}
    >
      {children}
    </Flex>
  );
});

export const EmptyStateWrapper = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps
>((props, forwardedRef) => {
  const { size = 'default' } = props;

  const Wrapper = size === 'large' ? Monitored : Unmonitored;

  return <Wrapper key={size} {...props} ref={forwardedRef} />;
});
